import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Award, Plus, Heart, MessageCircle, Star, Search } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CommunitySuccessStories = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [storyForm, setStoryForm] = useState({
    title: "",
    story: "",
    achievement: "",
    category: "Career",
    image_url: ""
  });

  const categories = ["All", "Career", "Academic", "Personal Growth", "Competition", "Scholarship"];

  useEffect(() => {
    fetchStories();
  }, [selectedCategory]);

  const fetchStories = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/community/success-stories${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStories(response.data);
    } catch (error) {
      toast.error("Failed to load stories");
    } finally {
      setLoading(false);
    }
  };

  const handleShareStory = async () => {
    try {
      const form = new FormData();
      form.append("title", storyForm.title);
      form.append("story", storyForm.story);
      form.append("achievement", storyForm.achievement);
      form.append("category", storyForm.category);
      if (storyForm.image_url) {
        form.append("image_url", storyForm.image_url);
      }

      await axios.post(`${API}/community/success-stories`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Story shared successfully!");
      setShowShareModal(false);
      setStoryForm({ title: "", story: "", achievement: "", category: "Career", image_url: "" });
      fetchStories();
    } catch (error) {
      toast.error("Failed to share story");
    }
  };

  const handleLikeStory = async (storyId) => {
    try {
      await axios.post(`${API}/community/success-stories/${storyId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Story liked!");
      fetchStories();
    } catch (error) {
      toast.error("Failed to like story");
    }
  };

  const filteredStories = stories.filter(story =>
    (story.title && story.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (story.story && story.story.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading stories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center shadow-md">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Success Stories</h1>
                <p className="text-gray-600 text-sm md:text-base">Get inspired by amazing achievements</p>
              </div>
            </div>
            <Button onClick={() => setShowShareModal(true)} className="shadow-md">
              <Plus className="w-4 h-4 mr-2" />
              Share Your Story
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6 border-none shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder="Search stories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat.toLowerCase() ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.toLowerCase())}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStories.length > 0 ? (
            filteredStories.map(story => (
              <Card key={story.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                {story.image_url && (
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Award className="w-16 h-16 text-primary" />
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline">{story.category}</Badge>
                    {story.is_featured && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg">{story.title}</CardTitle>
                  <CardDescription className="text-xs">
                    by {story.username} · {formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">Achievement</span>
                    </div>
                    <p className="text-sm text-primary font-medium">{story.achievement}</p>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {story.story}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLikeStory(story.id)}
                      className="text-xs"
                    >
                      <Heart className="w-4 h-4 mr-1" />
                      {story.likes} Likes
                    </Button>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" />
                      {story.comment_count}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full">
              <Card className="border-none shadow-md">
                <CardContent className="p-12 text-center">
                  <Award className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No stories found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm ? "Try adjusting your search" : "Be the first to share your success story!"}
                  </p>
                  <Button onClick={() => setShowShareModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Share Your Story
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Share Story Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Share Your Success Story</DialogTitle>
              <DialogDescription>Inspire others with your achievements</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={storyForm.title}
                  onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                  placeholder="Give your story a catchy title"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={storyForm.category} onValueChange={(value) => setStoryForm({ ...storyForm, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c !== "All").map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Your Achievement</Label>
                <Input
                  value={storyForm.achievement}
                  onChange={(e) => setStoryForm({ ...storyForm, achievement: e.target.value })}
                  placeholder="e.g., Got selected for Google internship"
                />
              </div>
              <div>
                <Label>Your Story</Label>
                <Textarea
                  value={storyForm.story}
                  onChange={(e) => setStoryForm({ ...storyForm, story: e.target.value })}
                  placeholder="Share your journey, challenges, and how you overcame them..."
                  rows={6}
                />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input
                  value={storyForm.image_url}
                  onChange={(e) => setStoryForm({ ...storyForm, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowShareModal(false)}>Cancel</Button>
              <Button onClick={handleShareStory}>Share Story</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default CommunitySuccessStories;
